import React, { useEffect, useState, useRef, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Menu, Dialog, Transition, Popover, Switch } from "@headlessui/react";
import clsx from "clsx";
import Layout from "../../layouts";
import Dropzone from "../../components/Dropzone";
import FieldError from "../../components/FieldError";
import Breadcrumb from "../../components/Breadcrumb";
import { useOrganisationStore } from "../../store";
import { validateOrganisationNewForm } from "../../services/validators";

export default function NewForm() {
  const formRef = useRef();
  const navigate = useNavigate();
  const [messages, setMessages] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const store = useOrganisationStore();

  useEffect(() => {
    if (!store.form.agree) {
      navigate("/organisation");
      return;
    }
  }, [navigate, store.form.agree]);

  useEffect(() => {
    window.addEventListener("beforeunload", alertBeforeLeaving);
    window.scrollTo(0, 0);
    return () => {
      window.removeEventListener("beforeunload", alertBeforeLeaving);
    };
  }, []);

  const alertBeforeLeaving = (e) => {
    e.preventDefault();
    e.returnValue = "";
  };

  const readFileASync = (file) => {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;

      reader.readAsDataURL(file);
    });
  };

  const onOrgProofSelected = async (file) => {
    const contentBuffer = await readFileASync(file);
    store.updateForm("orgProof", contentBuffer);
  };

  const onOrgProofRemoved = () => {
    store.updateForm("orgProof", null);
  };

  const onValidate = () => {
    const validated = validateOrganisationNewForm(store.form);

    const errors = validated.reduce(
      (accumulator, value) => ({
        ...accumulator,
        [value]: "Must not be empty",
      }),
      {}
    );
    setMessages(errors);

    if (Object.keys(errors).length > 0) {
      setShowAlert(true);
      formRef.current.scrollIntoView({ behavior: "smooth" });
    } else {
      setShowAlert(false);
      navigate(`/organisation/app/new/confirmation`);
    }
  };

  const onValidateField = (field) => {
    if (!store.form[field] || store.form[field].trim() === "") {
      setMessages({ ...messages, [field]: "Must not be empty" });
    } else {
      setMessages((current) => {
        const { [field]: key, ...rest } = current;

        return rest;
      });
    }
  };

  return (
    <Layout mType="organisation">
      <div className="bg-gray-100">
        <div className="container mx-auto">
          <div className="lg:py-5 lg:px-0 px-0 py-4">
            <ul className="form-steps">
              <li className="active">Declaration</li>
              <li className="active">Form</li>
              <li>Confirmation</li>
              <li>Completion</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container mx-auto" ref={formRef}>
        <div className="lg:py-8 lg:px-0 px-4 py-4">
          <Breadcrumb mType="organisation" appType="new" />

          <h3 className="font-bold text-lg mb-8">New Card Application</h3>
          {showAlert && (
            <div
              class="flex p-4 mb-8 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
              role="alert"
            >
              <svg
                aria-hidden="true"
                class="flex-shrink-0 inline w-5 h-5 mr-3"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span class="sr-only">Info</span>
              <div>
                <span class="font-medium">Error:</span> Please fix the errors
                below before continuing to the next step.
              </div>
            </div>
          )}
          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">Card Type</h3>
            </div>
            <div className="px-8 py-8" id="fg-cardType">
              <div class="flex items-center mb-4">
                <input
                  type="radio"
                  id="card-type-reader"
                  name="card_type"
                  value="reader"
                  class="w-4 h-4"
                  onChange={() => store.updateForm("cardType", "reader")}
                  checked={store.form.cardType === "reader"}
                />
                <label for="card-type-reader" class="ml-2">
                  Reader
                </label>
              </div>
              <div class="flex items-center">
                <input
                  type="radio"
                  id="card-type-borrower"
                  name="card_type"
                  value="borrower"
                  class="w-4 h-4"
                  onChange={() => store.updateForm("cardType", "borrower")}
                  checked={store.form.cardType === "borrower"}
                />
                <label for="card-type-borrower" class="ml-2">
                  Borrower
                </label>
              </div>

              <FieldError field={messages.cardType} mt={5} />
            </div>
          </div>
          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">Card Pickup Location</h3>
            </div>
            <div className="px-8 py-8" id="fg-cardType">
              <div class="flex items-center mb-4">
                <input
                  type="radio"
                  id="pickup-mmw"
                  name="pickup_location"
                  value="mmw"
                  class="w-4 h-4"
                  onChange={() => store.updateForm("pickupLocation", "mmw")}
                  checked={store.form.pickupLocation === "mmw"}
                />
                <label for="pickup-mmw" class="ml-2">
                  MMW Library
                </label>
              </div>
              <div class="flex items-center">
                <input
                  type="radio"
                  id="pickup-tkosc"
                  name="pickup_location"
                  value="tkosc"
                  class="w-4 h-4"
                  onChange={() => store.updateForm("pickupLocation", "tkosc")}
                  checked={store.form.pickupLocation === "tkosc"}
                />
                <label for="pickup-tkosc" class="ml-2">
                  TKOSC Library
                </label>
              </div>

              <FieldError field={messages.pickupLocation} mt={5} />
            </div>
          </div>
          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">General Information</h3>
            </div>
            <div className="lg:px-8 lg:py-8 px-8 py-4">
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    * Name of Organisation (English):
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="org"
                    placeholder=""
                    value={store.form.org}
                    onChange={(e) => store.updateForm("org", e.target.value)}
                    onBlur={() => onValidateField("org")}
                  />
                  <FieldError field={messages.org} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    * Name of Organisation (Chinese):
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="org_chi"
                    placeholder=""
                    value={store.form.orgChi}
                    onChange={(e) => store.updateForm("orgChi", e.target.value)}
                    onBlur={() => onValidateField("orgChi")}
                  />
                  <FieldError field={messages.orgChi} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    * Contact Person's Surname:
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="surname"
                    placeholder="e.g. Chan"
                    value={store.form.surname}
                    onChange={(e) =>
                      store.updateForm("surname", e.target.value)
                    }
                    onBlur={() => onValidateField("surname")}
                  />
                  <FieldError field={messages.surname} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    * Contact Person's Other Names:
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="other_names"
                    placeholder="e.g. Tai Man"
                    value={store.form.otherNames}
                    onChange={(e) =>
                      store.updateForm("otherNames", e.target.value)
                    }
                    onBlur={() => onValidateField("otherNames")}
                  />
                  <FieldError field={messages.otherNames} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    * Contact Person's Chinese Name:
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="chi_name"
                    value={store.form.chineseName}
                    onChange={(e) =>
                      store.updateForm("chineseName", e.target.value)
                    }
                    onBlur={() => onValidateField("chineseName")}
                  />
                  <FieldError field={messages.chineseName} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    * Contact Person's Position:
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="org_position"
                    value={store.form.orgPosition}
                    onChange={(e) =>
                      store.updateForm("orgPosition", e.target.value)
                    }
                    onBlur={() => onValidateField("orgPosition")}
                  />
                  <FieldError field={messages.orgPosition} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    * Contact Address:
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="contact_addr"
                    value={store.form.addrContact}
                    onChange={(e) =>
                      store.updateForm("addrContact", e.target.value)
                    }
                    onBlur={() => onValidateField("addrContact")}
                  />
                  <FieldError field={messages.addrContact} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* Phone Number:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="home_tel"
                    value={store.form.phone}
                    onChange={(e) => store.updateForm("phone", e.target.value)}
                    onBlur={() => onValidateField("phone")}
                  />
                  <FieldError field={messages.phone} />
                </div>
              </div>
              <div className="lg:flex">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* E-mail:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="email"
                    value={store.form.email}
                    onChange={(e) => store.updateForm("email", e.target.value)}
                    onBlur={() => onValidateField("email")}
                  />
                  <p className="text-blue-600 text-sm mt-1">
                    Email notifications such as payment link, confirmation and
                    application status etc will be sent to this email address.
                  </p>
                  <FieldError field={messages.email} />
                </div>
              </div>
            </div>
          </div>

          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">Required Document</h3>
            </div>
            <div className="px-8 py-8">
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    * Required Document:
                    <Popover className="relative inline-block">
                      <Popover.Button className="focus:outline-none">
                        <svg
                          class="w-6 h-6 inline-block ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          ></path>
                        </svg>
                      </Popover.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel className="absolute left-0 z-10 mt-3 w-screen max-w-sm px-4 sm:px-0 lg:max-w-xl">
                          <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                            <div className="relative bg-white p-7">
                              <p>XXXXXXXXXXXX</p>
                            </div>
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </Popover>
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  {store.form.orgProof ? (
                    <div>
                      <p>
                        <span className="rounded-lg border text-sm px-2 py-1 bg-gray-50">
                          1 file selected
                        </span>
                      </p>
                      <button
                        className="text-red-600 text-sm mt-2 focus:outline-none hover:text-red-700"
                        onClick={onOrgProofRemoved}
                      >
                        <svg
                          class="w-4 h-4 inline-block mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                          ></path>
                        </svg>
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/gif, application/pdf"
                        maxFiles="1"
                        className="block p-3"
                        name="recomm_letter"
                        onChange={(e) => onOrgProofSelected(e.target.files[0])}
                        onBlur={() => onValidateField("orgProof")}
                      />
                      <div>
                        <p className="text-gray-500">
                          File format: jpg / gif / png / pdf
                        </p>
                        <p className="text-gray-500">File size: 2MB</p>
                      </div>
                      <FieldError field={messages.orgProof} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center my-8">
            <button
              className="border rounded-full px-4 py-2 w-48 text-center inline-block text-gray-600 hover:bg-gray-200 transition duration-200 focus:outline-none"
              onClick={() => navigate(`/organisation/app/new`)}
            >
              Back
            </button>
            <button
              className="border border-orange-600 rounded-full px-4 py-2 w-48 text-center inline-block bg-orange-600 text-white ml-8 hover:bg-orange-700 transition duration-200 focus:outline-none"
              onClick={onValidate}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
